// =============================================
// General Ledger posting helper
// =============================================
// One place for the double-entry math + balance updates, shared by the manual
// "Make Journal Entries" UI and the Bank Txns "Post to GL" action. A journal
// entry is a balanced set of lines (Σ debit === Σ credit); posting creates the
// acctg_gl_journal header + acctg_gl_journal_line rows and moves each affected
// acctg_coa account's running current_balance. Voiding reverses all of that.
// =============================================
import { addRow, saveRow } from '../../db-viewer/data/dataAccess';

const NOW_ISO = () => new Date().toISOString().replace('T', ' ').substring(0, 19);
const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

export interface JournalLineInput {
  coa_id: number;
  debit: number;          // exactly one of debit/credit is > 0, the other 0
  credit: number;
  memo?: string | null;
  location_id?: number | null;   // profit center (business Location) — optional
  unit_id?: number | null;       // profit center (business Unit) — optional
}

export interface CoaLite {
  coa_id: number;
  normal_balance?: string;                 // 'Debit' | 'Credit'
  current_balance?: number | string | null;
}

export interface PostJournalParams {
  accountOwnerId: number;
  ownerType: string;
  ownerId: number;
  date: string;                            // YYYY-MM-DD
  memo?: string;
  source?: 'manual' | 'bank_txn';
  sourceTxnId?: number | null;
  documentId?: number | null;
  currency?: string;
  createdBy?: number | null;
  lines: JournalLineInput[];
  coaAccounts: CoaLite[];
}

export interface BalanceUpdate { coa_id: number; current_balance: number; }
export interface PostJournalResult {
  journal: Record<string, any>;
  lines: Record<string, any>[];
  balanceUpdates: BalanceUpdate[];
}

// Returns an error message if the lines don't form a balanced entry, else null.
export function validateBalanced(lines: JournalLineInput[]): string | null {
  if (!lines || lines.length < 2) return 'A journal entry needs at least two lines.';
  let totalDebit = 0, totalCredit = 0;
  for (const l of lines) {
    const d = Number(l.debit) || 0;
    const c = Number(l.credit) || 0;
    if (!l.coa_id) return 'Each line needs an account.';
    if (d < 0 || c < 0) return 'Amounts cannot be negative.';
    if (d > 0 && c > 0) return 'Each line is either a debit or a credit, not both.';
    if (d === 0 && c === 0) return 'Each line needs a debit or a credit amount.';
    totalDebit += d; totalCredit += c;
  }
  if (round2(totalDebit) === 0) return 'Entry total cannot be zero.';
  if (round2(totalDebit) !== round2(totalCredit)) return 'Debits must equal credits.';
  return null;
}

// Signed change to an account's current_balance for a posting, by normal balance:
//   Debit-normal (assets, expenses)   → debit raises it   → (debit − credit)
//   Credit-normal (liab, equity, inc) → credit raises it  → (credit − debit)
function balanceDelta(normalBalance: string | undefined, debit: number, credit: number): number {
  const d = Number(debit) || 0, c = Number(credit) || 0;
  return /credit/i.test(normalBalance || '') ? (c - d) : (d - c);
}

// Build the two GL lines for a bank-statement transaction. A statement's
// `direction` is from the bank's view; the owner's Bank/Cash account is an asset:
//   direction 'debit'  (money OUT) → Debit the mapped account, Credit the bank account
//   direction 'credit' (money IN)  → Debit the bank account,   Credit the mapped account
export function bankTxnToLines(
  txn: { direction?: string; amount?: number | string; coa_id: number; description?: string | null; location_id?: number | null; unit_id?: number | null },
  bankCoaId: number,
): JournalLineInput[] {
  const amt = round2(Number(txn.amount) || 0);
  // Profit center applies to the mapped income/expense line only — the bank/cash
  // line is the company's own account and carries no profit center.
  const mapped: JournalLineInput = { coa_id: Number(txn.coa_id), debit: 0, credit: 0, memo: txn.description ?? null, location_id: txn.location_id ?? null, unit_id: txn.unit_id ?? null };
  const bank: JournalLineInput = { coa_id: Number(bankCoaId), debit: 0, credit: 0, memo: txn.description ?? null };
  if (txn.direction === 'debit') { mapped.debit = amt; bank.credit = amt; }
  else { bank.debit = amt; mapped.credit = amt; }
  return [mapped, bank];
}

// Aggregate balance deltas per account (a journal could touch the same account twice).
function aggregateDeltas(lines: JournalLineInput[], coaAccounts: CoaLite[], reverse = false): BalanceUpdate[] {
  const deltaByCoa = new Map<number, number>();
  for (const l of lines) {
    const acct = coaAccounts.find(a => Number(a.coa_id) === Number(l.coa_id));
    let delta = balanceDelta(acct?.normal_balance, l.debit, l.credit);
    if (reverse) delta = -delta;
    const id = Number(l.coa_id);
    deltaByCoa.set(id, (deltaByCoa.get(id) || 0) + delta);
  }
  const updates: BalanceUpdate[] = [];
  deltaByCoa.forEach((delta, coaId) => {
    const acct = coaAccounts.find(a => Number(a.coa_id) === coaId);
    const prev = Number(acct?.current_balance) || 0;
    updates.push({ coa_id: coaId, current_balance: round2(prev + delta) });
  });
  return updates;
}

// Create a balanced journal entry: header + lines, stamp JE-number, move balances.
// Throws on an unbalanced entry or a server rejection (no silent swallow).
export async function postJournalEntry(params: PostJournalParams): Promise<PostJournalResult> {
  const {
    accountOwnerId, ownerType, ownerId, date, memo = '', source = 'manual',
    sourceTxnId = null, documentId = null, currency = 'CAD', createdBy = null,
    lines, coaAccounts,
  } = params;

  const err = validateBalanced(lines);
  if (err) throw new Error(err);

  const totalDebit = round2(lines.reduce((s, l) => s + (Number(l.debit) || 0), 0));
  const totalCredit = round2(lines.reduce((s, l) => s + (Number(l.credit) || 0), 0));
  const now = NOW_ISO();

  // 1. Header
  const journal = await addRow('acctg_gl_journal', {
    account_owner_id: accountOwnerId,
    owner_type: ownerType,
    owner_id: ownerId,
    journal_date: date,
    memo: memo || null,
    source,
    source_txn_id: sourceTxnId,
    document_id: documentId,
    currency,
    total_debit: totalDebit,
    total_credit: totalCredit,
    status: 'posted',
    is_active: 1,
    is_deleted: 0,
    created_by: createdBy,
    created_at: now,
    updated_at: now,
  }, 'journal_id');

  const journalId = Number(journal.journal_id);
  const journalNumber = `JE-${String(journalId).padStart(5, '0')}`;
  try {
    await saveRow('acctg_gl_journal', journalId, { journal_number: journalNumber, updated_at: now }, 'journal_id');
    journal.journal_number = journalNumber;
  } catch { /* friendly number is cosmetic — ignore if it fails */ }

  // 2. Lines
  const createdLines: Record<string, any>[] = [];
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    const row = await addRow('acctg_gl_journal_line', {
      journal_id: journalId,
      coa_id: l.coa_id,
      debit: round2(l.debit),
      credit: round2(l.credit),
      line_memo: l.memo || null,
      line_order: i + 1,
      location_id: l.location_id ?? null,
      unit_id: l.unit_id ?? null,
      is_active: 1,
      is_deleted: 0,
      created_at: now,
      updated_at: now,
    }, 'line_id');
    createdLines.push(row);
  }

  // 3. Move each affected account's running balance
  const balanceUpdates = aggregateDeltas(lines, coaAccounts);
  for (const u of balanceUpdates) {
    await saveRow('acctg_coa', u.coa_id, { current_balance: u.current_balance, updated_at: now }, 'coa_id');
  }

  return { journal, lines: createdLines, balanceUpdates };
}

// Void a posted entry: flag it void, reverse the balance deltas, and (if it came
// from a bank txn) flip that txn back to 'reviewed' so it can be re-posted.
export async function voidJournalEntry(
  journal: Record<string, any>,
  lines: JournalLineInput[],
  coaAccounts: CoaLite[],
): Promise<{ balanceUpdates: BalanceUpdate[] }> {
  const now = NOW_ISO();
  const journalId = Number(journal.journal_id);

  await saveRow('acctg_gl_journal', journalId, { status: 'void', updated_at: now }, 'journal_id');

  const balanceUpdates = aggregateDeltas(lines, coaAccounts, true);   // reverse
  for (const u of balanceUpdates) {
    await saveRow('acctg_coa', u.coa_id, { current_balance: u.current_balance, updated_at: now }, 'coa_id');
  }

  if (journal.source === 'bank_txn' && journal.source_txn_id != null) {
    try {
      await saveRow('acctg_bank_txn', Number(journal.source_txn_id), { status: 'reviewed', updated_at: now }, 'txn_id');
    } catch { /* ignore */ }
  }

  return { balanceUpdates };
}
